import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { PagesControlcenterApp } from "./PagesControlcenterApp";
import { StrictlyInternalGuard } from "./StrictlyInternalGuard";
import { ControlCenterIncompletePath } from "./components/ControlCenterIncompletePath";
import type { environments } from "@dealer-center/core-enviroment";
import { IncentivesManagerWrapper } from "@dealer-center/ddc-pages-incentives-manager-react";
import { AccountAssets } from "@dealer-center/ddc-pages-accountassets-react";
import { Welcome, YourTeam } from "@dealer-center/ddc-pages-welcome-react";
import { DdcFeeManager } from "@dealer-center/ddc-pages-fee-manager-react";
import { PagesConfigurationUiRoutes } from "@dealer-center/ddc-pages-configuration-react";
import { MessagePage } from "@dealer-center/core-custom-components";
import {
  InventoryApp,
  InventoryRoutes
} from "@dealer-center/inventory-pages-inventory-react";
import { PagesLeadManagerUiRoutes } from "@dealer-center/ddc-pages-lead-manager-react";
import { PagesBlogManagerUiRoutes } from "@dealer-center/ddc-pages-blog-manager-react";
import { PagesSpecialsManagerUiRoutes } from "@dealer-center/ddc-pages-specials-manager-react";

// Lazy-loaded so InventoryManager's bundled `semantic-ui-css/semantic.min.css`
// (a global Semantic reset) ships in its own route chunk and only loads on
// /cc/inventoryManager — instead of bleeding into every sibling /cc/* page via
// the shared static import chunk.
const InventoryManager = lazy(() =>
  import("@dealer-center/ddc-pages-inventory-manager-react").then(module => ({
    default: module.InventoryManager
  }))
);

export const PagesControlcenterUiRoutes: RouteObject[] = [
  {
    element: <PagesControlcenterApp />,
    children: [
      {
        index: true,
        element: <ControlCenterIncompletePath />
      },
      ...PagesConfigurationUiRoutes,
      {
        path: "incentivesManager",
        element: <IncentivesManagerWrapper />
      },
      {
        path: "inventoryManager",
        element: (
          <Suspense
            fallback={<MessagePage actionName="Loading" message="Loading" />}
          >
            <InventoryManager />
          </Suspense>
        )
      },
      {
        path: "inventory/configure/general",
        element: (
          <Suspense
            fallback={<MessagePage actionName="Loading" message="Loading" />}
          >
            <InventoryManager />
          </Suspense>
        )
      },
      // Inventory app (US2090536): home for the remaining inventory pages as
      // they migrate. The static "inventory/configure/general" route above
      // outranks this tree, so InventoryManager keeps that URL.
      {
        path: "inventory",
        element: <InventoryApp />,
        children: [
          ...InventoryRoutes,
          {
            path: "*",
            element: <ControlCenterIncompletePath />
          }
        ]
      },
      ...PagesLeadManagerUiRoutes,
      ...PagesBlogManagerUiRoutes,
      ...PagesSpecialsManagerUiRoutes,
      {
        path: "accountAssets",
        element: <AccountAssets />
      },
      {
        path: "feemanager",
        element: <DdcFeeManager />
      },
      {
        path: "welcome",
        children: [
          {
            index: true,
            element: <Welcome />
          },
          {
            path: "yourteam",
            element: <YourTeam />
          }
        ]
      }
    ]
  }
];

export const getControlCenterRoutes = (
  environment: environments,
  auth: React.ReactElement
) => {
  const asPaths = [
    {
      path: "/cc",
      element: auth,
      children: [
        {
          element: <StrictlyInternalGuard />,
          children: PagesControlcenterUiRoutes
        }
      ]
    },
    {
      path: "/cc/as/:asAccount/:asUser",
      element: auth,
      children: [
        {
          element: <StrictlyInternalGuard />,
          children: PagesControlcenterUiRoutes
        }
      ]
    }
  ];
  if (environment === "local" || environment === "qa") {
    return asPaths;
  } else {
    return asPaths;
  }
};
